import styled, { css } from "styled-components/macro";
import { Divider } from "@mui/material";
import {
  producingColor,
  plannedColor,
  unplannedColor,
  waitingColor,
  untaggedColor,
  noProductionScheduledColor,
  uncollectedColor,
  darkModeModalBackgroundColor,
} from "../../constants/colors";
import {
  FONT_SIZE_SMALL,
  FONT_SIZE_MEDIUM,
  MODAL_ARROW_HEIGHT,
  MODAL_ARROW_OFFSET_LEFT,
} from "../../constants/constants";
import { startCase } from "lodash";
import { PNL_COLOR_SQUARE_BORDER } from "../Nav/ColorLegend";
import { getPnlTitle } from "./HubletView/hubletViewUtils";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { PnlCategoriesEnum } from "types/enums";
import { useIsDarkMode } from "store/atoms";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useCustomerInfoJsonAtom } from "reports/utils/hooks/useCustomerInfoJson";
import { CUSTOMERS_CUSTOM_METRIC_EXPLAINER_IMGS } from "constants/customers";
import { DataDumpAggregateType } from "types/types";
import { memo, useMemo } from "react";

const BLOCK_WIDTH = 32;
export const getModalArowTopCSS = isDarkMode => css`
  &:before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    top: 0;
    left: calc(50% - ${MODAL_ARROW_OFFSET_LEFT}px);
    border: ${MODAL_ARROW_HEIGHT}px solid black;
    border-color: ${isDarkMode ? darkModeModalBackgroundColor : "white"}
      ${isDarkMode ? darkModeModalBackgroundColor : "white"} transparent
      transparent;
    transform-origin: 0 0;
    transform: scaleX(0.45)
      translateX(${(1 / 0.45) * MODAL_ARROW_HEIGHT + 11}px) rotate(-45deg);
    box-shadow:
      14px 3px 5px -6px rgba(0, 0, 0, 0.13),
      5px -7px 6px -2px rgba(0, 0, 0, 0.06);
  }
`;
const ExplainerTooltipStyles = styled.div<{ $isDarkMode: boolean }>`
  position: relative;
  pointer-events: none;
  ${({ $isDarkMode }) => getModalArowTopCSS($isDarkMode)}
  text-align: center;
  padding: 1rem;
  .title {
    padding-bottom: 0.75rem;
    font-size: ${FONT_SIZE_MEDIUM}px;
  }
  .event-based-tagging-note {
    font-size: ${FONT_SIZE_SMALL}px;
    font-style: italic;
    padding-bottom: 1rem;
    text-align: center;
    max-width: 200px;
  }
  .blocks {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    .plus {
      text-align: center;
      width: ${BLOCK_WIDTH}px;
      line-height: 1rem;
    }
    .blockAndLabel {
      display: flex;
      align-items: center;
      width: 100%;
      .block {
        border: ${PNL_COLOR_SQUARE_BORDER};
        width: ${BLOCK_WIDTH}px;
        height: ${BLOCK_WIDTH}px;
        &.producing {
          background: ${producingColor};
        }
        &.planned {
          background: ${plannedColor};
        }
        &.unplanned {
          background: ${unplannedColor};
        }
        &.waiting {
          background: ${waitingColor};
        }
        &.unknown {
          background: ${untaggedColor};
        }
        &.notScheduled {
          background: ${noProductionScheduledColor};
        }
        &.uncollected {
          background: ${uncollectedColor};
        }
      }
      .label {
        margin-left: 0.5rem;
        font-size: ${FONT_SIZE_SMALL}px;
        text-align: left;
      }
    }
  }
  hr {
    margin: 0.5rem -0.5rem;
    background-color: ${({ $isDarkMode }) => ($isDarkMode ? "white" : "black")};
    height: 2px;
  }
  img.custom-metric-explainer {
    width: 250px;
  }
`;
const BlockAndLabel = ({ category }) => {
  const { translate } = useFlightTranslation();
  const ckey = useCustomerKey();
  return (
    <div className={`blockAndLabel`}>
      <div className={`block ${category}`} />
      <div className={`label ${category}`}>
        {startCase(getPnlTitle(category, translate, ckey, null))}
      </div>
    </div>
  );
};
// metricTitle === 'TEEP' or 'Uptime' or 'Avail'
export const ExplainerTooltipContent = memo(
  ({
    dataDumpKey,
    displayName,
    description,
  }: {
    dataDumpKey?: keyof DataDumpAggregateType;
    displayName: string;
    description?: string;
  }) => {
    const ckey = useCustomerKey();
    const [isDarkMode] = useIsDarkMode();

    const isCustomWithDescription = useMemo(() => {
      return Boolean(
        description &&
          displayName &&
          dataDumpKey &&
          !METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS[dataDumpKey]
      );
    }, [description, displayName, dataDumpKey]);

    const customMetricExplainerImg = useMemo(() => {
      return (
        dataDumpKey &&
        CUSTOMERS_CUSTOM_METRIC_EXPLAINER_IMGS[ckey]?.[dataDumpKey]
      );
    }, [dataDumpKey, ckey]);
    return (
      <ExplainerTooltipStyles $isDarkMode={isDarkMode}>
        {customMetricExplainerImg ? (
          <img
            loading="lazy"
            className="custom-metric-explainer"
            src={`/images/custom-metric-explainers/${customMetricExplainerImg}`}
          />
        ) : isCustomWithDescription ? (
          <>
            <div className="title">{displayName}</div>
            {description ? (
              <div className="description">{` = ${description}`}</div>
            ) : null}
          </>
        ) : dataDumpKey &&
          METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS[dataDumpKey] ? (
          <BlockAndLabelExplainerTooltip
            {...{
              dataDumpKey,
              displayName,
            }}
          />
        ) : null}
      </ExplainerTooltipStyles>
    );
  }
);
ExplainerTooltipContent.displayName = "ExplainerTooltipContent";

/** custom tooltip content rendered for these keys in BlockAndLabelExplainerTooltip */
export const METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS: {
  [dataDumpKey: string]: keyof DataDumpAggregateType;
} = {
  uptimePercent: "uptimePercent",
  availabilityPercent: "availabilityPercent",
  teepPercent: "teepPercent",
  teepPercentBottomUp: "teepPercentBottomUp",
  efficiencyPercent: "efficiencyPercent",
  operationalAvailabilityPercent: "operationalAvailabilityPercent",
  unplannedPercent: "unplannedPercent",
  taggingPercent: "taggingPercent",
};

const BlockAndLabelExplainerTooltip = memo(
  ({
    dataDumpKey,
    displayName,
  }: {
    dataDumpKey?: keyof DataDumpAggregateType;
    displayName: string;
  }) => {
    const customerInfoJson = useCustomerInfoJsonAtom();
    const shouldUseEventBasedTaggingPercent =
      customerInfoJson &&
      "shouldUseEventBasedTaggingPercent" in customerInfoJson
        ? customerInfoJson.shouldUseEventBasedTaggingPercent
        : false;
    const shouldExcludeNPSInTaggingPercent = Boolean(
      customerInfoJson && "excludeNPSInTaggingPercent" in customerInfoJson
        ? customerInfoJson.excludeNPSInTaggingPercent
        : false
    );
    const { translate } = useFlightTranslation();

    const content = useMemo(() => {
      switch (dataDumpKey) {
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.teepPercent:
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.teepPercentBottomUp:
          return (
            <>
              <div className="title">{displayName}</div>
              <div className="blocks topRow">
                <ProducingBlock />
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <ProducingBlock />
                <div className="plus">+</div>
                <PlannedBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
                <div className="plus">+</div>
                <UnknownBlock />
                <div className="plus">+</div>
                <NotScheduledBlock />
              </div>
            </>
          );
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.uptimePercent:
          return (
            <>
              <div className="title">{displayName}</div>
              <div className="blocks topRow">
                <ProducingBlock />
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <ProducingBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
                <div className="plus">+</div>
                <UnknownBlock />
              </div>
            </>
          );
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.efficiencyPercent:
          return (
            <>
              <div className="title">{displayName}</div>
              <div className="blocks topRow">
                <ProducingBlock />
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <ProducingBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
              </div>
            </>
          );
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.operationalAvailabilityPercent:
          return (
            <>
              <div className="title">{displayName}</div>
              <div className="blocks topRow">
                <ProducingBlock />
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <ProducingBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
              </div>
            </>
          );
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.unplannedPercent:
          return (
            <>
              <div className="title">{displayName}</div>
              <div className="blocks topRow">
                <UnplannedBlock />
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
                <div className="plus">+</div>
                <UnknownBlock />
                <div className="plus">+</div>
                <UncollectedBlock />
              </div>
            </>
          );
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.availabilityPercent:
          return (
            <>
              <div className="title">{displayName}</div>
              <div className="blocks topRow">
                <ProducingBlock />
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <ProducingBlock />
                <div className="plus">+</div>
                <PlannedBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
                <div className="plus">+</div>
                <UnknownBlock />
              </div>
            </>
          );
        case METRICS_WITH_CUSTOM_TOOLTIP_EXPLAINERS.taggingPercent:
          return (
            <>
              <div className="title">{displayName}</div>
              {shouldUseEventBasedTaggingPercent && (
                <div className="event-based-tagging-note">
                  * {translate("tagging_percent_event_based_note")}
                </div>
              )}
              <div className="blocks topRow">
                <PlannedBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
                {shouldExcludeNPSInTaggingPercent && (
                  <>
                    <div className="plus">+</div>
                    <NotScheduledBlock />
                  </>
                )}
              </div>
              <Divider />
              <div className="blocks bottomRow">
                <PlannedBlock />
                <div className="plus">+</div>
                <UnplannedBlock />
                <div className="plus">+</div>
                <WaitingBlock />
                {shouldExcludeNPSInTaggingPercent && (
                  <>
                    <div className="plus">+</div>
                    <NotScheduledBlock />
                  </>
                )}
                <div className="plus">+</div>
                <UnknownBlock />
              </div>
            </>
          );
        default:
          return null;
      }
    }, [
      dataDumpKey,
      displayName,
      shouldUseEventBasedTaggingPercent,
      shouldExcludeNPSInTaggingPercent,
      translate,
    ]);

    return content;
  }
);
BlockAndLabelExplainerTooltip.displayName = "BlockAndLabelExplainerTooltip";

function UncollectedBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.uncollected} />;
}

function NotScheduledBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.notScheduled} />;
}

function UnknownBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.unknown} />;
}

function WaitingBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.waiting} />;
}

function UnplannedBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.unplanned} />;
}

function PlannedBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.planned} />;
}

function ProducingBlock() {
  return <BlockAndLabel category={PnlCategoriesEnum.producing} />;
}
